import dayjs from 'dayjs'
import { baseRoles } from '../../../constants/app'
import type { ImageLike } from '../../../utils/assetUrl'
import type { MyInvitation } from '../../../services/invitation/types'
import { pendingInvitationsCopy } from '../constants/pendingInvitationsCopy'

/**
 * SN-7849 — the invitations still waiting for the signed-in person, as web
 * Home's card and popup show them.
 */

/** How many names the card's line spells out before it counts the rest. */
const NAMES_SPELLED_OUT = 3

/** One invitation as a row of the popup. */
export interface PendingInvitationRow {
  id: string
  /** The team (or competition, game, organization) it invites onto. */
  name: string
  /** "Player", "Team Manager"…; null for a role the app has no name for. */
  role: string | null
  sender: string | null
  image: ImageLike
}

const instant = (value?: string | null): number =>
  value ? dayjs(value).valueOf() || 0 : 0

/** The invitations nobody has accepted, newest first; empty before the list has loaded. */
export const selectPendingInvitations = (
  invitations?: MyInvitation[] | null
): MyInvitation[] =>
  (invitations ?? [])
    .filter((invitation) => !invitation.accepted_at)
    .sort((a, b) => instant(b.created_at) - instant(a.created_at))

/**
 * Names it the way the invitation page does: a registration bucket team by its
 * competition, anything else by its destination.
 */
export const toPendingInvitationRow = (
  invitation: MyInvitation
): PendingInvitationRow => {
  const shown = invitation.display_destination ?? invitation.destination ?? null
  const roles = baseRoles as Record<number, string>

  return {
    id: invitation.id,
    name: shown?.name_full || shown?.name || pendingInvitationsCopy.unnamed,
    role:
      invitation.role_type_id != null
        ? roles[invitation.role_type_id] ?? null
        : null,
    sender: invitation.sender || null,
    image: shown?.image ?? null,
  }
}

/**
 * "North Stars, Ice Wolves and 13 more are waiting." One sentence, so the card
 * keeps it on one line (SN-8793): the title already counts them and the whole
 * card is the way in, so it needs no "Review And Accept…" after it, unlike
 * mobile's card.
 */
export const describePendingInvitations = (
  rows: PendingInvitationRow[]
): string => {
  if (rows.length === 0) {
    return ''
  }

  const names = rows.map((row) => row.name)
  const spelledOut =
    names.length <= NAMES_SPELLED_OUT
      ? names
      : [
          ...names.slice(0, NAMES_SPELLED_OUT - 1),
          pendingInvitationsCopy.andMore(
            names.length - (NAMES_SPELLED_OUT - 1)
          ),
        ]
  const list =
    spelledOut.length === 1
      ? spelledOut[0]
      : `${spelledOut.slice(0, -1).join(', ')} and ${
          spelledOut[spelledOut.length - 1]
        }`

  return `${list} ${rows.length === 1 ? 'is' : 'are'} waiting.`
}
